/**
 * Profile-Page · Profilbild-Upload, Name/Phone/Title, Passwort ändern.
 *
 * Backend-Endpoints:
 * - POST /api/users/:id/profile-image (FormData)
 * - PATCH /api/users/:id (name, phone, title)
 * - POST /api/auth/setup-password (für PW-change auch nach must_reset_pw=false)
 */
import { ChangeEvent, FormEvent, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { api, getAccessToken } from '../lib/api';
import { APP_VERSION, BUILD_TIMESTAMP } from '../lib/version';
import { useAuth } from '../lib/auth';

interface UserUpdate {
  full_name?: string;
  phone?: string | null;
  title?: string | null;
}

export function Profile() {
  const { user, refreshMe } = useAuth();
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [phone, setPhone] = useState((user as { phone?: string | null } | null)?.phone ?? '');
  const [title, setTitle] = useState((user as { title?: string | null } | null)?.title ?? '');
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  // Sprint 21 (S21.1) · Image-Load-Error → Initialen-Fallback
  const [imgError, setImgError] = useState(false);

  if (!user) return null;

  async function uploadImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`/api/users/${user.id}/profile-image`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: fd,
      });
      if (!resp.ok) {
        const j = (await resp.json().catch(() => ({}))) as { detail?: string };
        throw new Error(j.detail ?? `HTTP ${resp.status}`);
      }
      await refreshMe();
      setSuccess('Profilbild aktualisiert');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function saveProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: UserUpdate = {};
      if (fullName.trim() && fullName !== user.full_name) payload.full_name = fullName.trim();
      if (phone.trim() !== (user as { phone?: string | null }).phone) payload.phone = phone.trim() || null;
      if (title.trim() !== (user as { title?: string | null }).title) payload.title = title.trim() || null;
      if (Object.keys(payload).length === 0) {
        setSuccess('Keine Änderungen.');
        return;
      }
      await api(`/api/users/${user.id}`, { method: 'PATCH', body: payload });
      await refreshMe();
      setSuccess('Profil aktualisiert');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPw.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein.');
      return;
    }
    if (newPw !== newPwConfirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/setup-password', {
        method: 'POST',
        body: { new_password: newPw },
      });
      setNewPw('');
      setNewPwConfirm('');
      setSuccess('Passwort geändert.');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Passwort-Änderung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app-shell">
      <AppHeader subtitle="MEIN PROFIL" />
      <section className="app-shell__main">
        <div className="app-shell__card profile-card">
          <div className="app-shell__card-label">PROFIL · {user.role.replace('_', ' ').toUpperCase()}</div>
          <h1 className="app-shell__card-title">Mein Profil</h1>
          {success ? <p className="auth-success" role="status">{success}</p> : null}
          {error ? <p className="auth-error" role="alert">{error}</p> : null}

          <div className="profile-avatar-section">
            <div className="profile-avatar-display">
              {user.profile_image_url && !imgError ? (
                <img
                  src={user.profile_image_url}
                  alt="Profilbild"
                  className="profile-avatar-img"
                  onError={() => setImgError(true)}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  {user.full_name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                </div>
              )}
            </div>
            <div className="profile-avatar-actions">
              <label htmlFor="avatar-upload" className="auth-submit profile-avatar-upload-btn">
                {uploading ? 'LÄDT…' : 'BILD HOCHLADEN'}
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={uploadImage}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <p className="form-field__hint">JPEG/PNG/WebP · max 5 MB</p>
            </div>
          </div>

          <form onSubmit={saveProfile} className="auth-form">
            <h2 className="profile-section-title">Kontakt-Daten</h2>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-name">VOLLSTÄNDIGER NAME</label>
              <input
                id="p-name"
                className="auth-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                minLength={2}
                maxLength={200}
                required
              />
            </div>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-email">E-MAIL (nicht änderbar)</label>
              <input id="p-email" className="auth-input" value={user.email} disabled />
            </div>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-title">TITEL / POSITION</label>
              <input
                id="p-title"
                className="auth-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Sales Manager"
                maxLength={120}
              />
            </div>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-phone">TELEFON</label>
              <input
                id="p-phone"
                className="auth-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 …"
                maxLength={40}
              />
            </div>
            <button type="submit" disabled={busy} className="auth-submit">
              {busy ? 'SPEICHERT…' : 'PROFIL SPEICHERN'}
            </button>
          </form>

          <form onSubmit={changePassword} className="auth-form" style={{ marginTop: 32 }}>
            <h2 className="profile-section-title">Passwort ändern</h2>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-pw">NEUES PASSWORT</label>
              <input
                id="p-pw"
                className="auth-input"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                minLength={12}
                autoComplete="new-password"
              />
            </div>
            <div className="auth-field">
              <label className="auth-field__label" htmlFor="p-pw2">BESTÄTIGEN</label>
              <input
                id="p-pw2"
                className="auth-input"
                type="password"
                value={newPwConfirm}
                onChange={(e) => setNewPwConfirm(e.target.value)}
                minLength={12}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" disabled={busy || !newPw} className="auth-submit">
              {busy ? 'SPEICHERT…' : 'PASSWORT ÄNDERN'}
            </button>
          </form>
        </div>

        {/* Sprint 32 · App-Version sichtbar */}
        <div className="profile-version" aria-label="App-Version">
          LEADCraft v{APP_VERSION}
          <span className="profile-version__build" title={BUILD_TIMESTAMP}>
            · build {BUILD_TIMESTAMP.slice(0, 10)}
          </span>
        </div>
      </section>
    </main>
  );
}
